import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_ACCESS_PERMISSIONS = 'FETCH_ACCESS_PERMISSIONS';
export const FETCH_ACCESS_PERMISSIONS_SUCCESS = 'FETCH_ACCESS_PERMISSIONS_SUCCESS';
export const FETCH_ACCESS_PERMISSIONS_FAIL = 'FETCH_ACCESS_PERMISSIONS_FAIL';
export const CLEAR_ACCESS_PERMISSIONS = 'CLEAR_ACCESS_PERMISSIONS';
export const CREATE_ACCESS_PERMISSION = 'CREATE_ACCESS_PERMISSION';
export const CREATE_ACCESS_PERMISSION_SUCCESS = 'CREATE_ACCESS_PERMISSION_SUCCESS';
export const CREATE_ACCESS_PERMISSION_FAIL = 'CREATE_ACCESS_PERMISSION_FAIL';
export const UPDATE_ACCESS_PERMISSION = 'UPDATE_ACCESS_PERMISSION';
export const UPDATE_ACCESS_PERMISSION_SUCCESS = 'UPDATE_ACCESS_PERMISSION_SUCCESS';
export const UPDATE_ACCESS_PERMISSION_FAIL = 'UPDATE_ACCESS_PERMISSION_FAIL';
export const DELETE_ACCESS_PERMISSION = 'DELETE_ACCESS_PERMISSION';
export const DELETE_ACCESS_PERMISSION_SUCCESS = 'DELETE_ACCESS_PERMISSION_SUCCESS';
export const DELETE_ACCESS_PERMISSION_FAIL = 'DELETE_ACCESS_PERMISSION_FAIL';

// Reducer
export default function accessPermissionsReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_ACCESS_PERMISSIONS:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_ACCESS_PERMISSIONS_SUCCESS:
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_ACCESS_PERMISSIONS_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_ACCESS_PERMISSIONS:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_ACCESS_PERMISSION_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_ACCESS_PERMISSION_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_ACCESS_PERMISSION_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchAccessPermissions(payload) {
  return { type: FETCH_ACCESS_PERMISSIONS, payload };
}

export function fetchAccessPermissionsSuccess(payload) {
  return { type: FETCH_ACCESS_PERMISSIONS_SUCCESS, payload };
}

export function fetchAccessPermissionsFail(payload) {
  return { type: FETCH_ACCESS_PERMISSIONS_FAIL, payload };
}

export function clearAccessPermissions(payload) {
  return { type: CLEAR_ACCESS_PERMISSIONS, payload };
}

export function createAccessPermission(payload) {
  return { type: CREATE_ACCESS_PERMISSION, payload };
}

export function createAccessPermissionSuccess(payload) {
  return { type: CREATE_ACCESS_PERMISSION_SUCCESS, payload };
}

export function createAccessPermissionFail(payload) {
  return { type: CREATE_ACCESS_PERMISSION_FAIL, payload };
}

export function updateAccessPermission(payload) {
  return { type: UPDATE_ACCESS_PERMISSION, payload };
}

export function updateAccessPermissionSuccess(payload) {
  return { type: UPDATE_ACCESS_PERMISSION_SUCCESS, payload };
}

export function updateAccessPermissionFail(payload) {
  return { type: UPDATE_ACCESS_PERMISSION_FAIL, payload };
}

export function deleteAccessPermission(payload) {
  return { type: DELETE_ACCESS_PERMISSION, payload };
}

export function deleteAccessPermissionSuccess(payload) {
  return { type: DELETE_ACCESS_PERMISSION_SUCCESS, payload };
}

export function deleteAccessPermissionFail(payload) {
  return { type: DELETE_ACCESS_PERMISSION_FAIL, payload };
}

// Requests
export function requestFetchAccessPermissions({ limit, offset }) {
  return limit && offset
    ? callApi(`settings/access/?limit=${limit}&offset=${offset}`)
    : callApi('settings/access/');
}

export function requestSearchAccessPermissions({ limit, offset, filters }) {
  return callApi('settings/access/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
}

export function requestCreateAccessPermission(payload) {
  return callApi('settings/access/', 'POST', payload);
}

export function requestUpdateAccessPermission(payload) {
  return callApi('settings/access/', 'PATCH', payload);
}

export function requestDeleteAccessPermission(payload) {
  return callApi(`settings/access/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchAccessPermissionsSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchAccessPermissions, action.payload)
      : yield call(requestFetchAccessPermissions, action.payload);
    yield put(fetchAccessPermissionsSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchAccessPermissionsFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createAccessPermissionSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateAccessPermission, values);
    yield put(createAccessPermissionSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Access Permission successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createAccessPermissionFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateAccessPermissionSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateAccessPermission, values);
    yield put(updateAccessPermissionSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Access Permission successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateAccessPermissionFail(err.data.error));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteAccessPermissionSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteAccessPermission, ID);
    yield put(deleteAccessPermissionSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Access Permission successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteAccessPermissionFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete Access Permission',
      variant: 'warning',
    }));
  }
}

export function* accessPermissionsSaga() {
  yield takeLatest(FETCH_ACCESS_PERMISSIONS, fetchAccessPermissionsSaga);
  yield takeEvery(CREATE_ACCESS_PERMISSION, createAccessPermissionSaga);
  yield takeEvery(UPDATE_ACCESS_PERMISSION, updateAccessPermissionSaga);
  yield takeEvery(DELETE_ACCESS_PERMISSION, deleteAccessPermissionSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/accessPermissions.js